import React, { useEffect, useState } from 'react';
import { Avatar, Badge, Empty, Input, Modal, Select, Spin, message } from 'antd';
import { liveChatApi } from '../../utils/api';
import { OPERATOR_STATUS, operatorName } from './liveChatMeta';

/**
 * ارجاع گفتگو به همکار: انتخاب اپراتور واجد شرایط (دسترسی به منبع و بخش گفتگو) و توضیح اختیاری.
 * توضیح فقط برای اپراتورها در رشته گفتگو ثبت می‌شود و برای مشتری ارسال نمی‌شود.
 * اگر بخش گفتگو زیربخش داشته باشد، زیربخش هم همراه ارجاع انتخاب می‌شود.
 */
const TransferConversationModal = ({ open, conversationId, subDepartmentOptions = [], currentSubDepartmentId = null, onClose, onTransferred }) => {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [subDepartmentId, setSubDepartmentId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !conversationId) return undefined;
    let cancelled = false;
    setSelected(null);
    setNote('');
    setSubDepartmentId(currentSubDepartmentId ?? null);
    setLoading(true);
    liveChatApi
      .getTransferTargets(conversationId)
      .then((res) => {
        if (!cancelled) setTargets(res?.data ?? []);
      })
      .catch((error) => {
        if (!cancelled) message.error(error?.message || 'خطا در دریافت همکاران');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // زیربخش فعلی فقط هنگام باز شدن پیش‌فرض می‌شود
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, conversationId]);

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const res = await liveChatApi.transfer(
        conversationId,
        selected,
        note.trim(),
        subDepartmentOptions.length > 0 ? subDepartmentId ?? null : undefined,
      );
      message.success(res?.message || 'گفتگو ارجاع شد');
      onTransferred?.(res?.data);
      onClose();
    } catch (error) {
      message.error(error?.message || 'خطا در ارجاع گفتگو');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={<span className="custom-font">ارجاع گفتگو به همکار</span>}
      okText="ارجاع"
      cancelText="انصراف"
      onOk={submit}
      onCancel={onClose}
      okButtonProps={{ disabled: !selected, loading: submitting }}
      destroyOnHidden
      className="custom-font"
    >
      <Spin spinning={loading}>
        {!loading && targets.length === 0 ? (
          <Empty className="py-6" description={<span className="custom-font">همکاری با دسترسی به منبع و بخش این گفتگو نیست</span>} />
        ) : (
          <div className="flex flex-col gap-1.5 max-h-72 overflow-y-auto py-1" role="radiogroup" aria-label="انتخاب همکار">
            {targets.map((op) => {
              const status = OPERATOR_STATUS[op.status] || OPERATOR_STATUS.offline;
              const isSelected = selected === op.id;
              return (
                <button
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  key={op.id}
                  onClick={() => setSelected(op.id)}
                  className={`w-full text-start flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors custom-font ${
                    isSelected
                      ? 'border-brand-primary bg-primary-50 dark:bg-[rgba(13,136,126,0.14)]'
                      : 'border-gray-100 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5'
                  }`}
                >
                  <Avatar src={op.user?.image} size={34}>{operatorName(op).charAt(0)}</Avatar>
                  <span className="flex-1 min-w-0">
                    <span className="block truncate text-sm font-medium text-gray-800 dark:text-gray-100">{operatorName(op)}</span>
                    <span className="block text-[11px] text-gray-500">
                      {(op.open_conversations_count ?? 0).toLocaleString('fa-IR')} گفتگوی باز
                    </span>
                  </span>
                  <Badge color={status.color === 'default' ? '#9ca3af' : status.color} text={<span className="text-xs custom-font">{status.label}</span>} />
                </button>
              );
            })}
          </div>
        )}
      </Spin>
      {subDepartmentOptions.length > 0 && (
        <Select
          allowClear
          placeholder="زیربخش (اختیاری)"
          value={subDepartmentId ?? undefined}
          onChange={(v) => setSubDepartmentId(v ?? null)}
          options={subDepartmentOptions}
          className="custom-font w-full !mt-3"
        />
      )}
      <Input.TextArea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="توضیح برای همکار (اختیاری، برای مشتری ارسال نمی‌شود)"
        autoSize={{ minRows: 2, maxRows: 4 }}
        maxLength={1000}
        className="custom-font !mt-3"
      />
    </Modal>
  );
};

export default TransferConversationModal;
